import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid2,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import Header from "../Header";
import Footer from "../Footer";
import CommonDatePicker from "../datepicker/CommonDatePicker";
import { format } from "date-fns";
import config from "../common/Config";
import "../common/commonStyles.css";
import { buildDatatablePayload, postDashboardDatatable } from "../../utils/dashboardApi";

const TopSales = () => {
  const accessToken = localStorage.getItem("accessToken");
  const userId = localStorage.getItem("userId");
  const company_tax_type = localStorage.getItem('company_tax_type');
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const token = localStorage.getItem("token");
  useEffect(() => {
    if (accessToken === null) {
      window.location.href = "/react/login";
      return;
    }

    let s = localStorage.getItem('startDate');
    let e = localStorage.getItem('endDate');

    setStartDate(s || '');
    setEndDate(e || '');
  }, []);

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const formattedStartDate = format(value[0], 'yyyy-MM-dd');
      const formattedEndDate = format(value[1], 'yyyy-MM-dd');

      localStorage.setItem('startDate', formattedStartDate);
      localStorage.setItem('endDate', formattedEndDate);

      setStartDate(formattedStartDate);
      setEndDate(formattedEndDate);
      setPage(0);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { response, data: json } = await postDashboardDatatable(
        token,
        'get_top_sales',
        buildDatatablePayload({
          page,
          rowsPerPage,
          startDate,
          endDate,
          searchValue,
        })
      );

      if (!response?.ok) {
        throw new Error(`Failed to fetch data: ${response?.status ?? 'unknown'}`);
      }

      if (json && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.recordsFiltered || json.data.length);
      }
    } catch (error) {
      console.error('Error fetching top sales data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, startDate, endDate, searchValue]);

  useEffect(() => {
    if (accessToken === null) {
      return;
    }
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPage(0);
    }
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleSubmit = () => {
    setPage(0);
    fetchData();
  };

  return (
    <>
      <Box className="page-container" >
        <Paper
          className="paper-style"
        >
          <Box sx={{ px:2, py:1.2}}>
            <Grid2
              container
              spacing={1}
              display="flex"
              justifyContent="space-between"
              flexDirection={{ xs: "column", md: "row" }}
              alignItems="center"
            >
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography className="page-title">
                  Top Sales
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  className="input-border"
                  size="small"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify
                          icon="eva:search-fill"
                          sx={{ color: "#212b36", width: 20, height: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 3 }}>
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  character=" - "
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 1 }}>
                <Button
                  variant="contained"
                  className="button-variant-contained"
                  onClick={handleSubmit}
                  fullWidth
                >
                  Submit
                </Button>
              </Grid2>
            </Grid2>
            </Box>
            <TableContainer
            >
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              )}
              <Table
                size="small"
                className="table-cell-border"
              >
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell
                      align="left"
                      width="100px"
                    >
                      Sales Rep
                    </TableCell>
                    <TableCell
                      align="center"
                      width="100px"
                    >
                      Order
                    </TableCell>
                    <TableCell
                      align="center"
                      width="100px"
                    >
                      Accept
                    </TableCell>
                    <TableCell
                      align="center"
                      width="100px"
                    >
                      Picking
                    </TableCell>
                    <TableCell
                      align="center"
                      width="100px"
                    >
                      Delivered
                    </TableCell>
                    <TableCell
                      align="center"
                      width="100px"
                    >
                      Invoice
                    </TableCell>
                    <TableCell
                      align="center"
                      width="100px"
                    >
                      Return
                    </TableCell>
                    <TableCell
                      align="center"
                      width="100px"
                    >
                      {company_tax_type || 'Tax'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && data && data.length > 0 ? (
                    data.map((row, index) => (
                      <TableRow
                        key={index}
                        hover
                        className="table-cell-style"
                      >
                        <TableCell
                          align="left"
                    
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          align="center"
                       
                        >
                          {row.order_amt}
                        </TableCell>
                        <TableCell
                          align="center"
                        
                        >
                          {row.accept_amt}
                        </TableCell>
                        <TableCell
                          align="center"
                      
                        >
                          {row.picking_amt}
                        </TableCell>
                        <TableCell
                          align="center"
                     
                        >
                          {row.deliver_amt}
                        </TableCell>
                        <TableCell
                          align="center"
                      
                        >
                          {row.invoice_amt}
                        </TableCell>
                        <TableCell
                          align="center"
                       
                        >
                          {row.return_amt}
                        </TableCell>
                        <TableCell
                          align="center"
                    
                        >
                          {row.total_tax}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : !loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{ py: 8 }}
                      >
                        <Typography align="center" sx={{ color: "#637381", fontSize: "0.875rem" }}>
                          No data found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            {!loading && (
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                className="pagination-styles"
              />
            )}
    
        </Paper>
      </Box>

    </>
  );
};

export default TopSales;
